// Shared parser for the SCA analyzers' free-text `message` field. There's no
// structured per-issue metadata for advisory id / package / fix version
// (see `analyzer.Issue` — everything domain-specific is squeezed into the
// message string), so both the issue detail drawer and the project detail
// Images tab (T648) regex-parse the same conventions out of it.
//
// Four message shapes exist across the sca/ package, all sharing the same
// advisory-id / fixed-version / CVSS suffix convention:
//   - sca:vuln              "{ecosystem} {name}@{version}: {title} ({id})…"
//   - sca:image-base        "base image {ref} ({distro} {version}): {name}@{version}: {title} ({id})…"
//   - sca:image-base-unknown "base image \"{ref}\": could not determine …" (no package)
//   - container:vuln         "{label} ({ecosystem}): {name}@{version}: {title} ({id})…"
//     (label is either a distro+version pair or an image name/tag — T647's
//     image-SBOM ingest and the older tarball-based container:vuln share
//     this exact format string)

export interface ScaAdvisoryInfo {
  advisoryId: string | null;
  advisoryUrl: string | null;
  fixedVersion: string | null;
  cvssScore: string | null;
  ecosystem: string | null;
  packageName: string | null;
  installedVersion: string | null;
  /** Base/container image the finding was matched against. Only set for
   *  sca:image-base(-unknown) and container:vuln messages (T648). */
  baseImage: string | null;
}

interface PackageInfo {
  baseImage: string | null;
  ecosystem: string | null;
  packageName: string | null;
  installedVersion: string | null;
}

const IMAGE_BASE_RE = /^base image (\S+) \((\S+) (\S+)\):\s+(\S+)@([^:]+):/;
const IMAGE_BASE_UNKNOWN_RE = /^base image "([^"]+)":/;
const CONTAINER_RE = /^(.+?)\s+\((\S+)\):\s+(\S+)@([^:]+):/;
const PLAIN_RE = /^(\S+)\s+(\S+)@([^:]+):/;

function extractPackage(msg: string): PackageInfo {
  let m = msg.match(IMAGE_BASE_RE);
  if (m) {
    return {
      baseImage: m[1],
      ecosystem: `${m[2]} ${m[3]}`,
      packageName: m[4],
      installedVersion: m[5].trim(),
    };
  }
  m = msg.match(IMAGE_BASE_UNKNOWN_RE);
  if (m) {
    return { baseImage: m[1], ecosystem: null, packageName: null, installedVersion: null };
  }
  m = msg.match(CONTAINER_RE);
  if (m) {
    return { baseImage: m[1], ecosystem: m[2], packageName: m[3], installedVersion: m[4].trim() };
  }
  m = msg.match(PLAIN_RE);
  if (m) {
    return { baseImage: null, ecosystem: m[1], packageName: m[2], installedVersion: m[3].trim() };
  }
  return { baseImage: null, ecosystem: null, packageName: null, installedVersion: null };
}

export function parseScaMessage(msg: string): ScaAdvisoryInfo {
  const idMatch = msg.match(/\(((?:CVE|GHSA)-[\w-]+)\)/);
  const advisoryId = idMatch?.[1] ?? null;

  const fixedMatch = msg.match(/—\s*fixed in\s+(\S+)/);
  const fixedVersion = fixedMatch ? fixedMatch[1].replace(/\s*\[.*$/, '').trim() : null;

  const cvssMatch = msg.match(/\[CVSS\s+([\d.]+)\]/);
  const cvssScore = cvssMatch?.[1] ?? null;

  const pkg = extractPackage(msg);

  let advisoryUrl: string | null = null;
  if (advisoryId) {
    if (advisoryId.startsWith('CVE-')) {
      advisoryUrl = `https://nvd.nist.gov/vuln/detail/${advisoryId}`;
    } else if (advisoryId.startsWith('GHSA-')) {
      advisoryUrl = `https://github.com/advisories/${advisoryId}`;
    } else {
      advisoryUrl = `https://osv.dev/vulnerability/${advisoryId}`;
    }
  }

  return { advisoryId, advisoryUrl, fixedVersion, cvssScore, ...pkg };
}
